package org.teddapp.project.ui.finance

import org.teddapp.project.ui.LocalTeddFonts
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddMenu

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Finance_account
import com.tedd.db.Finance_category
import com.tedd.db.Finance_transaction
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.pomodoro.isoAddDays

/**
 * Page plein écran "Toutes les transactions" — stats détaillées,
 * filtres par plage de dates personnalisable, presets rapides, recherche.
 */
@Composable
internal fun AllTransactionsScreen(
    account: Finance_account,
    txns: List<Finance_transaction>,
    categories: List<Finance_category>,
    accounts: List<Finance_account>,
    currentAccountId: String?,
    onClose: () -> Unit,
    onEditTx: ((Finance_transaction) -> Unit)? = null,
) {
    var fromDate by remember { mutableStateOf("") }
    var toDate by remember { mutableStateOf("") }
    var type by remember { mutableStateOf(TxFilter.All) }
    var categoryId by remember { mutableStateOf<String?>(null) }
    var search by remember { mutableStateOf("") }

    val filtered = remember(txns, fromDate, toDate, type, categoryId, search) {
        val q = search.trim().lowercase()
        txns
            .asSequence()
            .filter { it.is_deleted == 0L }
            .filter { if (type == TxFilter.All) true else it.type == type.key }
            .filter { fromDate.isBlank() || it.date >= fromDate }
            .filter { toDate.isBlank() || it.date <= toDate }
            .filter { categoryId == null || it.cat_id == categoryId }
            .filter { q.isEmpty() || it.description?.lowercase()?.contains(q) == true }
            .sortedWith(compareByDescending<Finance_transaction> { it.date }.thenByDescending { it.id })
            .toList()
    }

    val summary = remember(filtered, currentAccountId, categories) {
        computeSummary(filtered, currentAccountId, categories)
    }
    val groups = remember(filtered) {
        val m = linkedMapOf<String, MutableList<Finance_transaction>>()
        for (t in filtered) m.getOrPut(t.date) { mutableListOf() }.add(t)
        m
    }
    val currency = account.currency
    val accentColor = parseFinHex(account.color)

    Dialog(
        onDismissRequest = onClose,
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = false),
    ) {
        Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            Column(modifier = Modifier.fillMaxSize()) {
                AllTxTopBar(
                    accountName = account.name,
                    accentColor = accentColor,
                    onClose = onClose,
                )
                LazyColumn(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 40.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    item(key = "stats") {
                        AllTxStatsCard(summary = summary, currency = currency, accent = accentColor)
                    }
                    item(key = "presets") {
                        DateRangePresets(
                            onPick = { from, to ->
                                fromDate = from
                                toDate = to
                            },
                        )
                    }
                    item(key = "range") {
                        DateRangeInputs(
                            fromDate = fromDate,
                            toDate = toDate,
                            onFromChange = { fromDate = it },
                            onToChange = { toDate = it },
                            onClear = { fromDate = ""; toDate = "" },
                        )
                    }
                    item(key = "filters") {
                        TypeAndCategoryFilters(
                            type = type,
                            onTypeChange = { type = it },
                            categories = categories,
                            categoryId = categoryId,
                            onCategoryChange = { categoryId = it },
                        )
                    }
                    item(key = "search") {
                        SearchInput(value = search, onChange = { search = it })
                    }
                    if (groups.isEmpty()) {
                        item(key = "empty") {
                            EmptyResult()
                        }
                    } else {
                        groups.entries.forEach { (day, list) ->
                            item(key = "g-$day") {
                                AllTxDayGroup(
                                    day = day,
                                    txns = list,
                                    net = list.sumOf { txSignedAmountFor(it, currentAccountId) },
                                    currency = currency,
                                    categories = categories,
                                    accounts = accounts,
                                    currentAccountId = currentAccountId,
                                    onEditTx = onEditTx,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Top bar
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun AllTxTopBar(
    accountName: String,
    accentColor: Color,
    onClose: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .statusBarsPadding()
            .padding(start = 12.dp, end = 16.dp, top = 14.dp, bottom = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(38.dp)
                .clip(CircleShape)
                .background(TeddTokens.Surface, CircleShape)
                .border(1.dp, TeddTokens.Line, CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onClose,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "chevron-left", size = 16.dp, color = TeddTokens.Ink, strokeWidth = 2.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                "TRANSACTIONS",
                color = TeddTokens.InkMuted,
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.6.sp,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                accountName,
                color = accentColor,
                fontSize = 18.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = (-0.3).sp,
                maxLines = 1,
            )
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Stats card — gros chiffres pour la plage filtrée
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun AllTxStatsCard(summary: TxSummary, currency: String, accent: Color) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(
                Brush.linearGradient(listOf(accent.copy(alpha = 0.10f), TeddTokens.LimeStrong.copy(alpha = 0.06f))),
                RoundedCornerShape(TeddTokens.RMd),
            )
            .border(1.dp, accent.copy(alpha = 0.22f), RoundedCornerShape(TeddTokens.RMd))
            .padding(16.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                "Résumé",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.4.sp,
            )
            Spacer(modifier = Modifier.weight(1f))
            Text(
                text = "${summary.txCount} transactions",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontFamily = LocalTeddFonts.current.mono,
            )
        }
        Spacer(modifier = Modifier.height(10.dp))
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            BigStat(
                label = "DÉPENSES",
                value = "−" + fmtMoney(summary.expense, currency, compact = true),
                valueColor = TeddTokens.Danger,
                modifier = Modifier.weight(1f),
            )
            BigStat(
                label = "ENTRÉES",
                value = "+" + fmtMoney(summary.income, currency, compact = true),
                valueColor = TeddTokens.Success,
                modifier = Modifier.weight(1f),
            )
        }
        Spacer(modifier = Modifier.height(8.dp))
        val absNet = if (summary.net >= 0L) summary.net else -summary.net
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                "Net sur la période",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.Medium,
                modifier = Modifier.weight(1f),
            )
            Text(
                text = (if (summary.net >= 0L) "+" else "−") + fmtMoney(absNet, currency),
                color = if (summary.net >= 0L) TeddTokens.Success else TeddTokens.Danger,
                fontSize = 16.sp,
                fontWeight = FontWeight.SemiBold,
                fontFamily = LocalTeddFonts.current.mono,
            )
        }
        if (summary.topCategoryName != null) {
            Spacer(modifier = Modifier.height(8.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(8.dp)
                        .clip(CircleShape)
                        .background(summary.topCategoryColor ?: TeddTokens.LimeStrong, CircleShape),
                )
                Text(
                    "Top catégorie · ${summary.topCategoryName}",
                    color = TeddTokens.InkMuted,
                    fontSize = 11.5.sp,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.weight(1f),
                )
                Text(
                    text = fmtMoney(summary.topCategoryAmount, currency, compact = true),
                    color = TeddTokens.Ink,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
        }
    }
}

@Composable
private fun BigStat(label: String, value: String, valueColor: Color, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp, vertical = 10.dp),
    ) {
        Text(label, color = TeddTokens.InkMuted, fontSize = 10.sp, fontWeight = FontWeight.Bold, letterSpacing = 0.5.sp)
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            value,
            color = valueColor,
            fontSize = 18.sp,
            fontWeight = FontWeight.SemiBold,
            fontFamily = LocalTeddFonts.current.mono,
            letterSpacing = (-0.4).sp,
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// Presets de plage de dates (relatif à aujourd'hui)
// ──────────────────────────────────────────────────────────────────────
private data class RangePreset(val label: String, val daysBack: Int?)

private val RANGE_PRESETS = listOf(
    RangePreset("Tout", null),
    RangePreset("7 j", 6),
    RangePreset("30 j", 29),
    RangePreset("90 j", 89),
    RangePreset("180 j", 179),
    RangePreset("1 an", 364),
)

@Composable
private fun DateRangePresets(onPick: (String, String) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TeddIcon(name = "calendar", size = 13.dp, color = TeddTokens.InkMuted)
        Spacer(modifier = Modifier.size(2.dp))
        RANGE_PRESETS.forEach { p ->
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {
                            val today = TODAY_ISO
                            if (p.daysBack == null) onPick("", "")
                            else onPick(isoAddDays(today, -p.daysBack), today)
                        },
                    )
                    .padding(horizontal = 10.dp, vertical = 5.dp),
            ) {
                Text(
                    p.label,
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Inputs date personnalisée (YYYY-MM-DD)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun DateRangeInputs(
    fromDate: String,
    toDate: String,
    onFromChange: (String) -> Unit,
    onToChange: (String) -> Unit,
    onClear: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        DateField(
            label = "DU",
            value = fromDate,
            onValueChange = onFromChange,
            modifier = Modifier.weight(1f),
        )
        DateField(
            label = "AU",
            value = toDate,
            onValueChange = onToChange,
            modifier = Modifier.weight(1f),
        )
        if (fromDate.isNotBlank() || toDate.isNotBlank()) {
            Box(
                modifier = Modifier
                    .size(38.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onClear,
                    ),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "trash", size = 14.dp, color = TeddTokens.InkMuted)
            }
        }
    }
}

@Composable
private fun DateField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier) {
        Text(label, color = TeddTokens.InkMuted, fontSize = 9.5.sp, fontWeight = FontWeight.Bold, letterSpacing = 0.5.sp)
        Spacer(modifier = Modifier.height(4.dp))
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(38.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(modifier = Modifier.weight(1f)) {
                if (value.isEmpty()) {
                    Text(
                        "YYYY-MM-DD",
                        color = TeddTokens.InkSubtle,
                        fontSize = 12.5.sp,
                        fontFamily = LocalTeddFonts.current.mono,
                    )
                }
                BasicTextField(
                    value = value,
                    onValueChange = { newValue ->
                        // Accept digits + dashes only, max 10 chars
                        if (newValue.length <= 10 && newValue.all { it.isDigit() || it == '-' }) {
                            onValueChange(newValue)
                        }
                    },
                    singleLine = true,
                    textStyle = TextStyle(
                        color = TeddTokens.Ink,
                        fontSize = 12.5.sp,
                        fontFamily = LocalTeddFonts.current.mono,
                    ),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                )
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Type segmented + category dropdown
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun TypeAndCategoryFilters(
    type: TxFilter,
    onTypeChange: (TxFilter) -> Unit,
    categories: List<Finance_category>,
    categoryId: String?,
    onCategoryChange: (String?) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(3.dp),
            horizontalArrangement = Arrangement.spacedBy(0.dp),
        ) {
            TxFilter.entries.forEach { f ->
                val on = type == f
                val label = when (f) {
                    TxFilter.All -> "Tout"
                    TxFilter.Expense -> "−"
                    TxFilter.Income -> "+"
                    TxFilter.Transfer -> "↔"
                }
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RXs))
                        .background(if (on) TeddTokens.Surface else Color.Transparent, RoundedCornerShape(TeddTokens.RXs))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onTypeChange(f) },
                        )
                        .padding(horizontal = 10.dp, vertical = 6.dp),
                ) {
                    Text(
                        text = label,
                        color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
        }
        Spacer(modifier = Modifier.weight(1f))
        CategoryDropdown(
            categories = categories.filter { it.is_deleted == 0L && (it.type == "expense" || it.type == "income") },
            selectedId = categoryId,
            onChange = onCategoryChange,
        )
    }
}

@Composable
private fun CategoryDropdown(
    categories: List<Finance_category>,
    selectedId: String?,
    onChange: (String?) -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    val selected = categories.firstOrNull { it.id == selectedId }
    val accent = selected?.let { parseFinHex(it.color) }
    Box {
        Row(
            modifier = Modifier
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(
                    if (selected != null) (accent ?: TeddTokens.LimeSoft).copy(alpha = 0.1f) else TeddTokens.Surface,
                    RoundedCornerShape(TeddTokens.RSm),
                )
                .border(
                    1.dp,
                    if (selected != null) (accent ?: TeddTokens.LimeStrong) else TeddTokens.Line,
                    RoundedCornerShape(TeddTokens.RSm),
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = { open = true },
                )
                .padding(horizontal = 10.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            TeddIcon(
                name = selected?.icon ?: "category",
                size = 12.dp,
                color = accent ?: TeddTokens.InkMuted,
            )
            Text(
                text = selected?.name ?: "Catégorie",
                color = if (selected != null) (accent ?: TeddTokens.Ink) else TeddTokens.InkMuted,
                fontSize = 11.5.sp,
                fontWeight = FontWeight.Medium,
            )
        }
        TeddMenu(
            expanded = open,
            onDismiss = { open = false },
            width = 260.dp,
            entries = listOf(TeddMenuEntry("Toutes catégories", icon = "category", selected = selected == null, onClick = { onChange(null) })) +
                categories.map { c ->
                    TeddMenuEntry(
                        c.name,
                        selected = selected?.id == c.id,
                        leading = { Box(modifier = Modifier.size(10.dp).clip(CircleShape).background(parseFinHex(c.color), CircleShape)) },
                        onClick = { onChange(c.id) },
                    )
                },
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// Search input
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun SearchInput(value: String, onChange: (String) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(38.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        TeddIcon(name = "search", size = 13.dp, color = TeddTokens.InkMuted)
        Box(modifier = Modifier.weight(1f)) {
            if (value.isEmpty()) {
                Text(
                    "Rechercher une description…",
                    color = TeddTokens.InkSubtle,
                    fontSize = 12.5.sp,
                )
            }
            BasicTextField(
                value = value,
                onValueChange = onChange,
                singleLine = true,
                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 12.5.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
            )
        }
        if (value.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .size(20.dp)
                    .clip(CircleShape)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = { onChange("") },
                    ),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.InkMuted)
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Empty state + day group (réutilise DayGroup de FinanceTabs)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun EmptyResult() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 40.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .size(48.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "search", size = 22.dp, color = TeddTokens.InkMuted)
        }
        Spacer(modifier = Modifier.height(12.dp))
        Text("Aucun résultat", color = TeddTokens.Ink, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            "Ajuste la plage de dates ou les filtres pour voir plus de transactions.",
            color = TeddTokens.InkMuted,
            fontSize = 11.5.sp,
        )
    }
}

@Composable
private fun AllTxDayGroup(
    day: String,
    txns: List<Finance_transaction>,
    net: Long,
    currency: String,
    categories: List<Finance_category>,
    accounts: List<Finance_account>,
    currentAccountId: String?,
    onEditTx: ((Finance_transaction) -> Unit)? = null,
) {
    DayGroup(
        day = day,
        txns = txns,
        net = net,
        currency = currency,
        categories = categories,
        accounts = accounts,
        currentAccountId = currentAccountId,
        onEditTx = onEditTx,
    )
}